import { ClassifierData } from 'types/classifiers';
import { writesFromStaged } from 'pages/pricelists/modal/employeePricing';
import { Contract, ContractCreateDto, ContractForm, ContractType } from 'types/contracts';
import { CustomFieldDto } from 'types/customFields';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getClassifierOptionByCode, getOptionsFromClassifier } from 'utils/formUtil';
import { PricingKind } from 'types/pricelists';
import { sortByField } from 'utils/sort';
import { toTimecardIncrementOption } from 'utils/timecardIncrementOptions';

/** The classifiers the contract form reads. */
export const CONTRACT_FORM_CLASSIFIERS = [
    'CONTRACT_TYPE',
    'SCOPE_TYPE',
    'DOCUMENT_TEMPLATE_TYPE',
    'INVOICING_INTERVAL_TYPE',
    'TIMECARD_SPECIFICATION_LAYOUT_TYPE',
    'CURRENCY',
    'JOB_ROLE_GROUP_TYPE',
    'CONTRACT_RELATED_KEYWORD_TYPE',
];

type Option = { label: string; value: string };

const toInputDate = (date: Date | string | undefined) => (date ? formatDateStringInput(new Date(date)) : null);

/** The edit form's values for a contract, as the contract edit page loads them. */
export const contractDtoToForm = (
    contract: Contract,
    {
        classifiers,
        customFieldValues,
    }: { classifiers: Record<string, ClassifierData>; customFieldValues: Record<string, unknown> }
): ContractForm => {
    const keywordCodes = contract.contractRelatedKeywords?.map((val) => val.contractRelatedKeywordCode) ?? [];
    return {
        billingNotes: contract.billingNotes ?? '',
        invoicingInfo: contract.invoicingInfo ?? '',
        contractName: contract.contractName ?? '',
        nameExtension: contract.nameExtension ?? '',
        contractType: getClassifierOptionByCode<string, string>(
            classifiers['CONTRACT_TYPE'],
            contract.contractTypeCode
        ),
        contractTemplate: contract.contractTemplate || null,
        isClientTemplate: contract.isClientTemplate || false,
        documentTemplateType: getClassifierOptionByCode<string, string>(
            classifiers['DOCUMENT_TEMPLATE_TYPE'],
            contract.documentTemplateTypeCode
        ),
        tenantCompany: contract.tenantCompany?.id
            ? { id: contract.tenantCompany.id, name: contract.tenantCompany.name }
            : null,
        signedByPersonParty: contract.signedByPersonParty?.id
            ? {
                  id: contract.signedByPersonParty.id,
                  name: contract.signedByPersonParty.name,
                  firstName: contract.signedByPersonParty.firstName,
                  lastName: contract.signedByPersonParty.lastName,
              }
            : null,
        companies: contract.companies?.map((val) => ({ id: val.company.id, name: val.company.name })) ?? [],
        parties:
            contract.parties?.map((val) => ({
                id: val.party.id,
                name: val.party.name,
                firstName: val.party.firstName,
                lastName: val.party.lastName,
                partyType: val.party.partyType,
                relationshipTypeCode: val.party.relationshipTypeCode,
            })) ?? [],
        clientReference: contract.clientReference ?? '',
        contractNumber: contract.contractNumber ?? '',
        dmsLink: contract.dmsLink ?? '',
        dmsNumber: contract.dmsNumber ?? '',
        contractSentAt: toInputDate(contract.contractSentAt),
        contractSignedAt: toInputDate(contract.contractSignedAt),
        contractTerminatedAt: toInputDate(contract.contractTerminatedAt),
        financialConditionId: contract.financialCondition?.id ?? null,
        paymentDeadlineInDays: contract.financialCondition?.paymentDeadlineInDays ?? null,
        prepaymentPaymentDeadlineInDays: contract.financialCondition?.prepaymentPaymentDeadlineInDays ?? null,
        invoicingIntervalType: getClassifierOptionByCode<string, string>(
            classifiers['INVOICING_INTERVAL_TYPE'],
            contract.financialCondition?.invoicingIntervalTypeCode
        ),
        discountPct: contract.discountPct || null,
        interestRatePct: contract.financialCondition?.interestRatePct ?? null,
        interestRateB2cPct: contract.financialCondition?.interestRateB2cPct ?? null,
        currencyType: getClassifierOptionByCode<string, string>(
            classifiers['CURRENCY'],
            contract.financialCondition?.currencyTypeCode
        ),
        timecardSpecificationLayoutType: getClassifierOptionByCode<string, string>(
            classifiers['TIMECARD_SPECIFICATION_LAYOUT_TYPE'],
            contract.timecardSpecificationLayoutTypeCode
        ),
        timecardIncrement:
            contract.timecardIncrement != null ? toTimecardIncrementOption(contract.timecardIncrement) : null,
        pricelistId: contract.pricelist?.id ?? null,
        pricelist: contract.pricelist
            ? {
                  name: contract.pricelist.name || '',
                  scopeTypeCode: 'CONTRACT',
                  currencyTypeCode: contract.pricelist.currencyTypeCode || '',
                  pricingKind: contract.pricelist.pricingKind,
              }
            : null,
        pricelistRates: contract.pricelist?.rates || [],
        pricelistPeriods: contract.pricelist?.periods ?? null,
        pricelistFollows: contract.pricelist?.followsPricelist ?? null,
        pricelistEdited: false,
        pricingHash: undefined,
        beneficialOwners: sortByField(contract.beneficialOwners ?? [], 'name'),
        beneficialOwnerNotes: contract.beneficialOwnerNotes || '',
        readOnlyCreatedAt: toInputDate(contract.createdAt),
        contractRelatedKeywords: (
            getOptionsFromClassifier(classifiers['CONTRACT_RELATED_KEYWORD_TYPE']) as Option[]
        ).filter((val) => typeof val.value === 'string' && keywordCodes.includes(val.value)),
        adminFeePercentage: contract.adminFeePercentage || null,
        additionalNotes: contract.additionalNotes || '',
        bankAccount: contract.bankAccount?.id ? { id: contract.bankAccount.id, name: contract.bankAccount.name } : null,
        ...customFieldValues,
    };
};

/** The create and update request for the form's values, as the contract form submits them. */
export const contractFormToRequest = (form: ContractForm, customFields: CustomFieldDto[]): ContractCreateDto => ({
    contractTypeCode: form?.contractType ? form.contractType.value : null,
    nameExtension:
        form?.contractType?.value === ContractType.SERVICE_AGREEMENT && form?.nameExtension?.trim()
            ? form.nameExtension.trim()
            : null,
    documentTemplateTypeCode: form?.documentTemplateType ? form.documentTemplateType.value : null,
    tenantCompanyId: form?.tenantCompany ? form.tenantCompany.id : null,
    signedByPersonPartyId: form?.signedByPersonParty ? form.signedByPersonParty.id : null,
    companies: form?.companies?.map(({ id }) => ({ id })) ?? [],
    parties: form?.parties?.map(({ id }) => ({ id })) ?? [],
    clientReference: form?.clientReference || null,
    contractNumber: form?.contractNumber || null,
    dmsLink: form?.dmsLink || null,
    dmsNumber: form?.dmsNumber || null,
    contractSentAt: form?.contractSentAt ? new Date(form.contractSentAt) : null,
    contractSignedAt: form?.contractSignedAt ? new Date(form.contractSignedAt) : null,
    contractTerminatedAt: form?.contractTerminatedAt ? new Date(form.contractTerminatedAt) : null,
    financialConditionId: form?.financialConditionId ?? null,
    paymentDeadlineInDays: form?.paymentDeadlineInDays ?? null,
    prepaymentPaymentDeadlineInDays: form?.prepaymentPaymentDeadlineInDays ?? null,
    invoicingIntervalTypeCode: form?.invoicingIntervalType ? form.invoicingIntervalType.value : null,
    discountPct: form?.discountPct || null,
    interestRatePct: form?.interestRatePct ?? null,
    interestRateB2cPct: form?.interestRateB2cPct ?? null,
    currencyTypeCode: form?.currencyType ? form.currencyType.value : null,
    timecardSpecificationLayoutTypeCode: form?.timecardSpecificationLayoutType
        ? form.timecardSpecificationLayoutType.value
        : null,
    timecardIncrement:
        form?.contractType?.value === ContractType.SERVICE_AGREEMENT ? (form?.timecardIncrement?.value ?? null) : null,
    pricelistId: form?.pricelistId ?? null,
    pricelist: form?.pricelist ? pricelistRequest(form) : null,
    ...(customFields.length > 0 ? { customFields: customFields } : {}),
    billingNotes: form?.billingNotes || null,
    invoicingInfo: form?.invoicingInfo || null,
    contractTemplateId: form?.contractTemplate?.id || null,
    isClientTemplate: form?.isClientTemplate,
    beneficialOwners: form?.beneficialOwners?.map((namedEntity) => namedEntity.id) || null,
    beneficialOwnerNotes: form?.beneficialOwnerNotes || null,
    contractRelatedKeywords: form.contractRelatedKeywords
        ? form.contractRelatedKeywords.map(({ value }) => ({ contractRelatedKeywordCode: value }))
        : [],
    additionalNotes: form?.additionalNotes || null,
    adminFeePercentage: form?.adminFeePercentage || null,
    bankAccountId: form?.bankAccount ? form.bankAccount.id : null,
});

// A role-priced list is sent with its rows, as it always was. An employee-priced one carries no role rows, and its
// link or periods only once the form changed them: the server keeps what a save leaves out, and a list following a
// model refuses periods of its own.
function pricelistRequest(form: ContractForm): NonNullable<ContractCreateDto['pricelist']> {
    const pricelist = form.pricelist as NonNullable<ContractForm['pricelist']>;
    const base = {
        name: pricelist.name,
        currencyTypeCode: pricelist.currencyTypeCode,
        scopeTypeCode: 'CONTRACT',
        pricingKind: pricelist.pricingKind,
    };
    if (pricelist.pricingKind !== PricingKind.EMPLOYEE) {
        return { ...base, rates: form.pricelistRates };
    }
    if (!form.pricelistEdited) {
        return { ...base, rates: [] };
    }
    if (form.pricelistFollows) {
        return { ...base, rates: [], followsPricelist: { id: form.pricelistFollows.id } };
    }
    return { ...base, rates: [], followsPricelist: null, periods: writesFromStaged(form.pricelistPeriods ?? []) };
}

/** The types the server takes without a price list and financial conditions; every other type requires both. */
const TYPES_WITHOUT_PRICING: readonly string[] = [
    ContractType.UBO,
    ContractType.AML,
    ContractType.REPRESENTATION,
    ContractType.COOPERATION,
];

export const needsPricing = (contractTypeCode: string | null | undefined): boolean =>
    !!contractTypeCode && !TYPES_WITHOUT_PRICING.includes(contractTypeCode);

const isSet = (value: unknown) => value != null && value !== '';

/** The server's own test of a request's financial conditions: without them a type that needs pricing is a 400. */
export const hasFinancialConditions = (request: ContractCreateDto): boolean =>
    isSet(request.currencyTypeCode) &&
    request.pricelist != null &&
    isSet(request.paymentDeadlineInDays) &&
    isSet(request.invoicingIntervalTypeCode) &&
    isSet(request.interestRatePct) &&
    isSet(request.interestRateB2cPct);

/**
 * The form fields each section of the contract page edits. The type's dependents ride with it: the timecard
 * increment, the admin fee and the discount, which the financial-conditions modal does not edit. The price list and
 * financial conditions are edited by their cards' modals, so they belong to no section unless the Contract section
 * widens to them (`contractSectionFields`). Concluded by clears the bank account, which therefore sits beside it.
 */
export const CONTRACT_SECTION_FIELDS = {
    contract: [
        'contractType',
        'nameExtension',
        'isClientTemplate',
        'contractTemplate',
        'contractNumber',
        'contractRelatedKeywords',
        'dmsLink',
        'timecardIncrement',
        'adminFeePercentage',
        'discountPct',
    ],
    parties: ['parties', 'tenantCompany', 'companies', 'signedByPersonParty', 'bankAccount'],
    'beneficial-owners': ['beneficialOwners', 'beneficialOwnerNotes'],
    billing: ['billingNotes', 'invoicingInfo', 'clientReference'],
    dates: ['readOnlyCreatedAt', 'contractSentAt', 'contractSignedAt', 'contractTerminatedAt'],
    notes: ['additionalNotes'],
    'custom-fields': [],
} satisfies Record<string, (keyof ContractForm)[]>;

export type ContractSectionId = keyof typeof CONTRACT_SECTION_FIELDS;

/** The currency, price list and financial conditions the Contract section takes on when it widens. */
export const CONTRACT_PRICING_FIELDS = [
    'currencyType',
    'pricelist',
    'pricelistRates',
    'pricelistPeriods',
    'pricelistFollows',
    'pricelistEdited',
    'pricingHash',
    'paymentDeadlineInDays',
    'prepaymentPaymentDeadlineInDays',
    'invoicingIntervalType',
    'interestRatePct',
    'interestRateB2cPct',
] satisfies (keyof ContractForm)[];

/**
 * Whether the Contract section edits the pricing too: the chosen type needs it and the contract as stored has
 * none, so saving the type alone would be refused.
 */
export const widensToPricing = (chosenTypeCode: string | null | undefined, stored: ContractForm): boolean =>
    needsPricing(chosenTypeCode) && !hasFinancialConditions(contractFormToRequest(stored, []));

/** The section fields for one save, with the Contract section widened when `widensToPricing` holds. */
export const contractSectionFields = (
    edited: ContractForm,
    stored: ContractForm
): Record<ContractSectionId, readonly string[]> =>
    widensToPricing(edited.contractType?.value, stored)
        ? {
              ...CONTRACT_SECTION_FIELDS,
              contract: [...CONTRACT_SECTION_FIELDS.contract, ...CONTRACT_PRICING_FIELDS],
          }
        : CONTRACT_SECTION_FIELDS;
